import * as React from 'react';
import styled, { css } from 'styled-components';

import useCurrentUser from 'hooks/useCurrentUser';
import { isPermitted } from 'util/PermissionsMixin';
import BootstrapModalConfirm from 'components/bootstrap/BootstrapModalConfirm';
import { Button, Label, Table } from 'components/bootstrap';
import { Icon } from 'components/common';
import { DataWell } from 'common/components';
import { useRemoveEvidence } from 'security-app/hooks/useInvestigationsAPI';
import type { InvestigationsDetailsAPIType } from 'security-app/hooks/api/investigationsAPI.types';
import { TD, TH, RowTitle } from 'security-app/components/common';

import EventDetails from './EventDetails';
import EventsModal from './EventsModal';

import { IconButton } from '../styled.components';

const StyledCollapsableSectionHeading = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ExpandedTR = styled.tr(
  ({ theme }) => css`
    > td {
      border-top: 1px solid ${theme.colors.gray[80]} !important;
      padding: 10px 8px 8px 35px !important;
    }

    dd {
      margin-bottom: 0.25em;
    }

    dl {
      > dl,
      > ul {
        padding-left: 1.5em;
      }
    }

    ul {
      list-style-type: disc;
    }
  `,
);

const EventsTable = styled(Table)(
  ({ theme }) => css`
    tr {
      &:hover {
        background-color: ${theme.colors.gray[90]};
      }

      &${ExpandedTR} {
        &:hover {
          background-color: ${theme.colors.global.contentBackground};
        }
      }
    }
  `,
);

const StyledLabel = styled(Label)`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  margin-left: 0.5rem;
`;

type Props = {
  investigation: InvestigationsDetailsAPIType;
  isExpanded: boolean;
  onToggleExpanded: (sectionName: string) => void;
};

function Events({ investigation, isExpanded, onToggleExpanded }: Props) {
  const { permissions } = useCurrentUser();
  const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(false);
  const [deleteEventId, setDeleteEventId] = React.useState<string>(null);
  const [expandedRows, setExpandedRows] = React.useState<string[]>([]);

  const canManageEvidence = React.useMemo(
    () => !investigation.archived && isPermitted(permissions, 'investigations:edit'),
    [investigation.archived, permissions],
  );

  const { removeEvidence, removingEvidence } = useRemoveEvidence();

  const expandRow = (eventId) =>
    expandedRows.includes(eventId)
      ? setExpandedRows(expandedRows.filter((id) => id !== eventId))
      : setExpandedRows([...expandedRows, eventId]);

  const removeEvent = async () => {
    await removeEvidence({ invId: investigation.id, payload: { events: [deleteEventId] } });
    setShowDeleteDialog(false);
    setDeleteEventId(null);
  };

  const onDelete = (eventId: string) => (e: React.BaseSyntheticEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
    setDeleteEventId(eventId);
  };

  const onCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteEventId(null);
  };

  const userHasEditEventPermissions = (eventDefinitionId: string) =>
    isPermitted(permissions, `eventdefinitions:edit:${eventDefinitionId}`);

  const userHasReadEventPermissions = (eventDefinitionId: string) =>
    isPermitted(permissions, `eventdefinitions:read:${eventDefinitionId}`);

  const renderEvents = (events) =>
    events.map((event) => (
      <tbody key={event.id}>
        <tr style={{ cursor: 'pointer' }} key={event.id}>
          <TD data-testid="event-message">
            <RowTitle onClick={() => expandRow(event.id)}>{event.message}</RowTitle>
          </TD>
          <TD>{event.key || <i>No key</i>}</TD>
          <TD>{event.event_definition_type}</TD>
          <TD>{event.timestamp}</TD>
          {canManageEvidence && (
            <TD>
              <IconButton $danger data-testid={`delete-${event.id}`} onClick={onDelete(event.id)}>
                <Icon name="close" />
              </IconButton>
            </TD>
          )}
        </tr>
        {expandedRows.includes(event.id) && (
          <ExpandedTR key={`${event.id}-details`}>
            <td colSpan={5} aria-label={`${event.message}-details`}>
              <EventDetails
                event={event}
                hasEditPermissions={userHasEditEventPermissions(event.event_definition_id)}
                hasReadPermissions={userHasReadEventPermissions(event.event_definition_id)}
              />
            </td>
          </ExpandedTR>
        )}
      </tbody>
    ));

  return (
    <DataWell $color="content">
      <StyledCollapsableSectionHeading>
        <IconButton data-testid="toggle-events" onClick={() => onToggleExpanded('events')}>
          {isExpanded ? <Icon name="keyboard_arrow_up" /> : <Icon name="keyboard_arrow_down" />}
          <h2 style={{ marginLeft: '1rem' }}>Events</h2>
          <StyledLabel>{investigation.events.length}</StyledLabel>
        </IconButton>
        {canManageEvidence && (
          <Button data-testid="add-event" onClick={() => setShowAddModal(true)}>
            <Icon name="add" size="sm" />
            Add Events
          </Button>
        )}
      </StyledCollapsableSectionHeading>
      {isExpanded && (
        <div style={{ marginTop: '0.5rem' }}>
          {investigation.events.length === 0 ? (
            <div style={{ color: 'gray' }}>There are no events to list</div>
          ) : (
            <EventsTable condensed hover className="table table-striped" data-testid="events-list">
              <thead>
                <tr>
                  <TH>Description</TH>
                  <TH>Key</TH>
                  <TH>Event Definition Type</TH>
                  <TH>Timestamp</TH>
                  <TH />
                </tr>
              </thead>
              {renderEvents(investigation.events)}
            </EventsTable>
          )}
        </div>
      )}
      <EventsModal show={showAddModal} onCancel={() => setShowAddModal(false)} />
      <BootstrapModalConfirm
        showModal={showDeleteDialog}
        title="Delete Event"
        onConfirm={removeEvent}
        onCancel={onCancelDelete}
        confirmButtonDisabled={removingEvidence}>
        Are you sure you want to delete this Event?
      </BootstrapModalConfirm>
    </DataWell>
  );
}

export default Events;
